import * as d3 from 'd3-selection'
import * as shape from 'd3-shape'
import { interpolate } from 'd3-interpolate'
import pieLabels from './pie-labels'
import sliceLabels from './slice-labels'

const xPadding = 10
    , yPadding = 10

import animations from './animations'

export {animations}

const getStyles = (type, chart, grid) => {
  let styles = {
      outerRadius: Math.min(grid.width / 2 - xPadding, grid.height / 2 - yPadding),
      useStrokeWidth: chart.getPropertyValue('--pie-donut-use-stroke-width'),
      maskStrokeWidth: chart.getPropertyValue('--pie-donut-mask-stroke-width')
    }

  if (type === 'donut') {
    Object.assign(styles, {
      padAngle: 0.08, // donut segment spacing
      innerRadius: styles.outerRadius - styles.outerRadius * 0.2
    })
  } else {
    Object.assign(styles, {
      padAngle: 0,
      innerRadius: 0
    })
  }

  return styles
}

/*
 * Determines if we're showing slice labels
 * We show slice labels if the serie.title is set or `axes.pie.value: true`
 */
const hasSliceLabels = (series, config) => {
  return !!series.find(serie => serie.title) || config.value
}

export const preAnimate = function (chart, selection, series) {
  let seriesData = series.selection().data()
    , type = seriesData[0].type
    , dimensions = chart.datum().dimensions
    , config = chart.datum().axes.pie
    , grid = chart.layout.getGrid(dimensions, hasSliceLabels(seriesData, config) ? (16 / 9) : 1)
    , styles = getStyles(type, chart, grid)
    , arc = shape.arc()
                 .innerRadius(styles.innerRadius)
                 .outerRadius(styles.outerRadius)
    , defs = d3.select(chart.node().ownerSVGElement).selectAll('defs')
    , previousPies = defs.node().__pies
    , pieGenerator = shape.pie()
                          .value(d => d.raw)
                          .padAngle(styles.padAngle)
                          .sort(null) // Maintain incoming serie order

  // Determine all the pie data
  let pies = grid.blocks.map(block => {
    // Draw a pie for every item in the range
    let points = seriesData.map(serie => {
      let point = serie.points.length === 1 ? serie.points[0] :
        serie.points.find(point => point.rangeId === block.range.id) || {}
      point.serieId = serie.id
      point.title = serie.title
      point.subtitle = point.formatted // Use the formatted value on the point. Impact will set this based on UI settings
      point.color = point.color || serie.color
      return point
    }).filter(p => p.raw) // We don't want to draw slices with no value. See Impact #40130

    return {
      chartId: chart.id,
      title: block.range.title,
      dimensions: block.dimensions,
      subtitle: block.range.subtitle,
      rangeId: block.range.id,
      type,
      slices: pieGenerator(points)
    }
  })

  // Finally create a tweener for arc transitions
  let arcTweener = function (node, to) {
    return function () {
      let from = node._current
        , interpolateArc = interpolate(from, to)
        , interpolateInnerRadius = interpolate(previousPies ? previousPies.styles.innerRadius : styles.innerRadius, styles.innerRadius)
        , interpolateOuterRadius = interpolate(previousPies ? previousPies.styles.outerRadius : styles.outerRadius, styles.outerRadius)

      node._current = interpolateArc(0)

      return function (t) {
        let inner = interpolateInnerRadius(t)
          , outer = interpolateOuterRadius(t)

        return arc.innerRadius(inner)
                  .outerRadius(outer)(interpolateArc(t))
      }
    }
  }

  // Put pie level labels in the grid
  selection.call(pieLabels, grid, chart)
           .call(sliceLabels, pies, styles, chart)

  pies.styles = styles
  pies.range = grid.blocks.map(block => block.range)

  // Store pie data on the plot selection, so the post animate has access
  selection.node().__pies = pies


  // Data that should be set on each animation
  return {
    pies,
    styles,
    previousPies,
    arcTweener,
    arc
  }

}

export const postAnimate = function (chart, selection) {
  let pies = selection.node().__pies
  // Store it on the defs for next run
  d3.select(chart.node().ownerSVGElement).selectAll('defs').node().__pies = pies
}
